import { createContext, FC, PropsWithChildren, useContext } from 'react'
import { useParams } from 'react-router'
import { skipToken } from '@reduxjs/toolkit/query'

import {
  getPreQuoteOverrideKey,
  mapFeatureFlags,
  overrideFeatureFlags,
  resetPreQuotePersistentFeatureFlags,
  setPreQuotePersistentFeatureFlags,
} from './featureFlags.helpers'
import {
  defaultPostQuoteFeatureFlagValues,
  defaultPreQuoteFeatureFlagValues,
  FeatureFlagContextValue,
  postQuoteFlagKeys,
  preQuoteFlagKeys,
} from './featureFlags.types'
import Loader from 'components/Loader'

import { useFeatureFlagsQuery } from 'app/services'
import { addDatadogFeatureFlagEvaluation } from 'utils'
import { getLocalStorageItem } from 'utils/localStorage'

export const LOCAL_STORAGE_KEY = 'FF_KEYS'
export const LOCAL_STORAGE_PRE_QUOTE_KEY = 'PRE_QUOTE_KEYS'

const FeatureFlagContext = createContext<FeatureFlagContextValue | null>(null)

interface Props extends PropsWithChildren {
  preQuoteKey: string
  postQuoteKey?: string
}

export const FeatureFlagsProvider: FC<Props> = ({ children, preQuoteKey, postQuoteKey }) => {
  const { quoteId } = useParams<{ quoteId: string }>()

  if (!quoteId) {
    throw new Error('FeatureFlagsProvider was used on a route without a quote ID')
  }

  const preQuoteFlagsResponse = useFeatureFlagsQuery({
    flags: preQuoteFlagKeys,
    userKey: preQuoteKey,
  })

  const postQuoteFlagsResponse = useFeatureFlagsQuery(
    postQuoteKey
      ? {
          flags: postQuoteFlagKeys,
          userKey: postQuoteKey,
        }
      : skipToken,
  )

  if (preQuoteFlagsResponse.isFetching || postQuoteFlagsResponse.isFetching) {
    return <Loader />
  }

  const preQuotePersistentFeatureFlags = getLocalStorageItem(getPreQuoteOverrideKey(quoteId))
  const featureFlagsOverrides = getLocalStorageItem(LOCAL_STORAGE_KEY)

  let preQuoteFeatureFlags = mapFeatureFlags({
    isSuccess: preQuoteFlagsResponse.isSuccess,
    featureFlagsData: preQuoteFlagsResponse.data,
    defaultFlags: defaultPreQuoteFeatureFlagValues,
  })

  let postQuoteFeatureFlags = mapFeatureFlags({
    isSuccess: postQuoteFlagsResponse.isSuccess,
    featureFlagsData: postQuoteFlagsResponse.data,
    defaultFlags: defaultPostQuoteFeatureFlagValues,
  })

  const preQuoteOverrides = { ...preQuotePersistentFeatureFlags, ...featureFlagsOverrides }
  const postQuoteOverrides = { ...featureFlagsOverrides }

  if (preQuotePersistentFeatureFlags !== null || featureFlagsOverrides !== null) {
    preQuoteFeatureFlags = overrideFeatureFlags(
      preQuoteFeatureFlags,
      preQuoteFlagKeys,
      preQuoteOverrides,
    )
  }

  if (featureFlagsOverrides !== null && postQuoteFeatureFlags !== null) {
    postQuoteFeatureFlags = overrideFeatureFlags(
      postQuoteFeatureFlags,
      postQuoteFlagKeys,
      postQuoteOverrides,
    )
  }

  const isPostQuoteFlagsDefault = postQuoteKey === undefined || !postQuoteFlagsResponse.data

  addDatadogFeatureFlagEvaluation({
    ...preQuoteFeatureFlags,
    ...(isPostQuoteFlagsDefault ? {} : postQuoteFeatureFlags),
  })

  return (
    <FeatureFlagContext.Provider
      value={{
        preQuoteFeatureFlags,
        postQuoteFeatureFlags,
        isPostQuoteFlagsDefault,
        setPreQuotePersistentFeatureFlags,
        resetPreQuotePersistentFeatureFlags,
      }}
    >
      {children}
    </FeatureFlagContext.Provider>
  )
}

export const useFeatureFlags = () => {
  const featureFlagsValue = useContext(FeatureFlagContext)

  if (featureFlagsValue === null) {
    throw new Error("'useFeatureFlags' should have 'FeatureFlagsProvider' as a parent")
  }

  return featureFlagsValue
}

// Only used to initialise FF in the tracking provider
export const useTrackingFeatureFlags = () => {
  const featureFlagsValue = useContext(FeatureFlagContext)

  return featureFlagsValue
}
